import { canPayRequest, validRequestId } from "../../scripts/api_scan_workflow.js";

export const POLL_INTERVAL = 3_000;
export const POLL_DURATION = 60_000;

export function getScanPaymentReturn(search) {
    const params = new URLSearchParams(search);
    if (params.getAll("paiement").length === 1) {
        if (params.get("paiement") === "annule") return { kind: "interrupted" };
        if (params.get("paiement") === "retour") {
            const id = params.get("session_id")?.trim();
            return params.getAll("session_id").length === 1 && id && id !== "{CHECKOUT_SESSION_ID}"
                ? { kind: "payment", id } : { kind: "missing_session" };
        }
    }
    if (params.getAll("signature").length === 1 && params.get("signature") === "retour") {
        const id = params.get("request_id");
        return params.getAll("request_id").length === 1 && validRequestId(id)
            ? { kind: "signature", id } : { kind: "missing_request" };
    }
    return { kind: "none" };
}

export function requestReturnState(request, mode) {
    if (request.scan_status === "blocked" || request.payment_status === "requires_review") return "review";
    if (request.payment_status === "paid") return "confirmed";
    if (mode === "signature" && canPayRequest(request)) return "ready";
    if (mode === "signature" && ["declined", "voided", "expired"].includes(request.signature_status)) return "signature_stopped";
    if (["failed", "expired", "canceled", "cancelled"].includes(request.payment_status)) return "payment_stopped";
    return "waiting";
}

function waitForRetry(ms, signal) {
    return new Promise((resolve, reject) => {
        signal.throwIfAborted();
        const timer = setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, ms);
        function abort() { clearTimeout(timer); reject(signal.reason); }
        signal.addEventListener("abort", abort, { once: true });
    });
}

// A deadline includes HTTP time; no request can outlive this return check.
export async function pollScanRequest({ load, mode, signal, onState, now = Date.now, wait = waitForRetry }) {
    const controller = new AbortController();
    const deadline = now() + POLL_DURATION;
    const forwardAbort = () => controller.abort(signal.reason);
    signal.addEventListener("abort", forwardAbort, { once: true });
    const timer = setTimeout(() => controller.abort(new Error("deadline")), POLL_DURATION);
    let request;
    let transientError = false;
    try {
        signal.throwIfAborted();
        onState({ status: "checking" });
        while (now() < deadline) {
            try {
                request = await load(controller.signal);
                controller.signal.throwIfAborted();
                transientError = false;
                const status = requestReturnState(request, mode);
                onState({ status, request });
                if (status !== "waiting") return { status, request };
            } catch (error) {
                if (controller.signal.aborted) throw error;
                const status = error.response?.status;
                if (error.code === "authentication" || status === 401 || status === 403) {
                    onState({ status: "authentication" });
                    return;
                }
                // 404 may precede the webhook. Network failures are also retried
                // within this same bounded read-only cycle, never as a POST.
                if (![404, 408, 429, 502, 503, 504].includes(status) && (status || !error.isAxiosError)) {
                    onState({ status: "error" });
                    return;
                }
                transientError = true;
                onState({ status: "waiting", request });
            }
            const remaining = deadline - now();
            if (remaining <= 0) break;
            await wait(Math.min(POLL_INTERVAL, remaining), controller.signal);
            controller.signal.throwIfAborted();
        }
        if (!signal.aborted) onState({ status: transientError ? "error" : "pending", request });
    } catch {
        if (!signal.aborted) onState({ status: transientError ? "error" : "pending", request });
    } finally {
        clearTimeout(timer);
        signal.removeEventListener("abort", forwardAbort);
        controller.abort();
    }
}
